import { Property } from '../../../types';
import { HealthIssue } from '../../../types/inventoryHealth';
import { isApartmentOrStudio } from './adminFeeRule';

export function checkPriceRule(property: Property): HealthIssue | null {
  const price = property.precio_venta;
  if (!price || price < 35000000) {
    const formatted = price ? `$${price.toLocaleString('es-CO')}` : '$0';
    return {
      ruleId: 'price_out_of_range',
      ruleName: 'Precio de Venta Inusual (< $35M)',
      severity: 'critical',
      message: `Precio de venta de ${formatted}. Posible omisión de ceros o valor de arriendo en venta.`,
      field: 'precio_venta',
      currentValue: price,
      suggestedFix: 'Revisar si el precio de venta tiene todos los ceros correctos.',
    };
  }
  return null;
}

export function checkAreaRule(property: Property): HealthIssue | null {
  const rawArea = property.area_construida;
  const areaStr = property.area_construida_original || '';
  const parsed = rawArea || parseFloat(String(areaStr).replace(/[^0-9.]/g, '')) || 0;

  if (parsed <= 0) {
    return {
      ruleId: 'invalid_area',
      ruleName: 'Área Construida Vacía o en 0 m²',
      severity: 'critical',
      message: 'El inmueble no registra metros cuadrados construidos válidos.',
      field: 'area_construida',
      currentValue: parsed,
      suggestedFix: 'Registrar los m² de área construida o privada del inmueble.',
    };
  }
  return null;
}

export function isCommercialOrRuralWithoutMandatoryBaths(tipo?: string | null): boolean {
  if (!tipo) return false;
  const norm = tipo.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  return (
    norm.includes('deposito') ||
    norm.includes('local') ||
    norm.includes('oficina') ||
    norm.includes('finca') ||
    norm.includes('lote') ||
    norm.includes('bodega') ||
    norm.includes('parqueadero') ||
    norm.includes('garaje') ||
    norm.includes('terreno')
  );
}

export function checkRoomsAndBathsRule(property: Property): HealthIssue | null {
  const banos = property.num_banos;
  const isAptoOrStudio = isApartmentOrStudio(property.tipoinmueble);
  const isStudio = property.tipoinmueble?.toLowerCase().includes('apartaestudio');
  const skipBaths = isCommercialOrRuralWithoutMandatoryBaths(property.tipoinmueble);

  if (!skipBaths && (banos === null || banos === undefined || banos <= 0)) {
    return {
      ruleId: 'missing_rooms_baths',
      ruleName: 'Sin Baños Registrados (0 o nulo)',
      severity: 'warning',
      message: 'Propiedad residencial sin baños registrados.',
      field: 'num_banos',
      currentValue: banos,
      suggestedFix: 'Ingresar el número de baños del inmueble.',
    };
  }

  if (isAptoOrStudio && !isStudio && (!property.num_habitaciones || property.num_habitaciones <= 0)) {
    return {
      ruleId: 'missing_rooms_baths',
      ruleName: 'Apartamento sin Habitaciones (0 o nulo)',
      severity: 'warning',
      message: 'Un apartamento estándar debe registrar al menos 1 habitación.',
      field: 'num_habitaciones',
      currentValue: property.num_habitaciones,
      suggestedFix: 'Ingresar el número de habitaciones del apartamento.',
    };
  }

  return null;
}

export function checkPhotosRule(property: Property): HealthIssue | null {
  const photos = Array.isArray(property.images) ? property.images.filter((img) => typeof img === 'string' && img.trim().length > 5) : [];
  if (photos.length === 0) {
    return {
      ruleId: 'insufficient_photos',
      ruleName: 'Sin Fotografías (0 fotos)',
      severity: 'critical',
      message: 'El inmueble no tiene ninguna fotografía cargada en el portal.',
      field: 'photos',
      currentValue: 0,
      suggestedFix: 'Cargar al menos el set básico de fotografías para publicación comercial.',
    };
  }
  if (photos.length < 5) {
    return {
      ruleId: 'insufficient_photos',
      ruleName: 'Pocas Fotografías (< 5 fotos)',
      severity: 'warning',
      message: `El inmueble solo cuenta con ${photos.length} fotografía(s). Se recomiendan mínimo 5.`,
      field: 'photos',
      currentValue: photos.length,
      suggestedFix: 'Completar la galería con fotos de áreas sociales, habitaciones y cocina.',
    };
  }
  return null;
}

export function checkEstratoRule(property: Property): HealthIssue | null {
  const estrato = property.estrato;
  if (estrato === null || estrato === undefined || estrato < 1 || estrato > 6) {
    return {
      ruleId: 'missing_estrato',
      ruleName: 'Estrato No Asignado o Fuera de Rango',
      severity: 'warning',
      message: `Estrato actual: ${estrato || 'No asignado'}. En Colombia debe ser entre 1 y 6.`,
      field: 'estrato',
      currentValue: estrato,
      suggestedFix: 'Asignar el estrato socioeconómico correcto (1 al 6).',
    };
  }
  return null;
}
